/**
 * Row-click detail view for the Pool Audit log tab (PoolAuditPage.tsx) — the FULL record, including the
 * fields the table row has no room for (result/http_status/zoom_code/error/ip/request_id/intent_at/
 * completed_at) and the raw `details` JSON verbatim in a <pre>, alongside the same compact human lines
 * the table cell shows (formatAuditDetails) so the two never disagree on what a change meant.
 */
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/ui";
import {
  auditActionLabel,
  formatAuditDetails,
  type AccountLookup,
} from "@/features/pool/poolAuditFormat";
import { formatInstant } from "@/lib/format";
import type { PoolAuditEntry } from "@/api/types";

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-(length:--text-2xs) text-(--text-subtle)">{label}</dt>
      <dd className="text-(length:--text-sm) font-medium text-(--text)">{value}</dd>
    </div>
  );
}

export function AuditDetailModal({
  entry,
  resolveAccount,
  onClose,
}: {
  entry: PoolAuditEntry;
  /** r10 22/7: same account_id → "name (#number)" resolver PoolAuditPage's table cell uses, so the
   *  modal's detail lines never disagree with the row that opened it. */
  resolveAccount?: AccountLookup;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const lines = formatAuditDetails(entry.details, resolveAccount);
  const accountLabel =
    entry.target_account_name || entry.target_account_number
      ? [
          entry.target_account_name,
          entry.target_account_number && `#${entry.target_account_number}`,
        ]
          .filter(Boolean)
          .join(" · ")
      : (entry.target_account_id ?? null);

  return (
    <Modal
      open
      onClose={onClose}
      title={t("pool.audit.detailModalTitle", { id: entry.id })}
      size="lg"
    >
      <div className="flex flex-col gap-5">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
          <Field label={t("pool.audit.colTime")} value={formatInstant(entry.ts)} />
          <Field label={t("pool.audit.colActor")} value={entry.actor} />
          <Field
            label={t("pool.audit.colAction")}
            value={`${auditActionLabel(t, entry.action)} (${entry.action})`}
          />
          {entry.target_email && (
            <Field label={t("pool.audit.detailTargetEmail")} value={entry.target_email} />
          )}
          {entry.target_email_old && (
            <Field label={t("pool.audit.detailTargetEmailOld")} value={entry.target_email_old} />
          )}
          {accountLabel && <Field label={t("pool.audit.detailAccount")} value={accountLabel} />}
          {entry.period_id != null && (
            <Field label={t("pool.audit.detailPeriodId")} value={String(entry.period_id)} />
          )}
          {entry.trigger && <Field label={t("pool.audit.colTrigger")} value={entry.trigger} />}
          {entry.intent_at && (
            <Field label={t("pool.audit.detailIntentAt")} value={formatInstant(entry.intent_at)} />
          )}
          <Field
            label={t("pool.audit.detailResult")}
            value={entry.result ?? t("pool.audit.pendingResult")}
          />
          {entry.http_status != null && (
            <Field label={t("pool.audit.detailHttpStatus")} value={String(entry.http_status)} />
          )}
          {entry.zoom_code != null && (
            <Field label={t("pool.audit.detailZoomCode")} value={String(entry.zoom_code)} />
          )}
          {entry.completed_at && (
            <Field
              label={t("pool.audit.detailCompletedAt")}
              value={formatInstant(entry.completed_at)}
            />
          )}
          {entry.ip && <Field label={t("pool.audit.detailIp")} value={entry.ip} />}
          {entry.request_id && (
            <Field label={t("pool.audit.detailRequestId")} value={entry.request_id} />
          )}
        </dl>

        {entry.error && (
          <p role="alert" className="text-(length:--text-sm) text-(--danger)">
            {entry.error}
          </p>
        )}

        {lines.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <h3 className="text-(length:--text-xs) font-semibold text-(--text-strong)">
              {t("pool.audit.colDetail")}
            </h3>
            <ul className="flex flex-col gap-1 text-(length:--text-xs) text-(--text-muted)">
              {lines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <h3 className="text-(length:--text-xs) font-semibold text-(--text-strong)">
            {t("pool.audit.detailRawJson")}
          </h3>
          <pre className="max-h-56 overflow-auto rounded-(--radius-sm) border border-(--border-hairline) bg-(--surface-sunken) p-2.5 text-(length:--text-2xs) text-(--text-muted)">
            {JSON.stringify(entry.details, null, 2)}
          </pre>
        </div>
      </div>
    </Modal>
  );
}
